/**
 * PDF Template Editor V2 — invoice-api host for the block-native editor
 * from `@xhubio-saas/pdf-editor-frontend`.
 *
 * Mounted at `/pdf/v2/templates/:id`. This app has no legacy editor
 * route any more (`/pdf/templates/:id` was torn down with the v1 pages),
 * so this is the only editor. Diskriminator: `editorVersion` inside the
 * BlockTemplate JSONB. See
 * `REQUIREMENTS/API/done/design-v1-v2-editor-coexistence.md`.
 *
 * Behaviour:
 *   - v2 templates → mount EditorApp directly.
 *   - v1 templates (no `editorVersion` or `'v1'`) → show a warn-modal
 *     once before mounting; on confirm the editor opens and the next
 *     save auto-promotes the template to `editorVersion: 'v2'`. The old
 *     editor can no longer open it after that.
 *   - Legacy Craft.js node trees (no `body`) → show an upgrade hint
 *     instead of EditorApp because automatic conversion isn't safe.
 *
 * Compared to the saas-pdf-api host this page intentionally omits the
 * AI/snippet/comment/brand-kit features — invoice-api doesn't expose
 * those tRPC routes yet. They're all optional on `EditorApp`.
 */

import { ModalShell } from '@xhubio-saas/core-frontend/ui-components'
import {
  type BlockTemplate,
  EditorApp,
  type RenderApiClient,
  type TemplateApiClient,
} from '@xhubio-saas/pdf-editor-frontend'
import { useCallback, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useBlocker, useNavigate, useParams } from 'react-router-dom'
import { trpc } from '@/trpc'

// Thin typed view of just the tRPC procedures we need. invoice-api's
// router has more, but typing the whole surface here would duplicate
// generated types — a focused interface keeps this page legible.
interface TrpcWithPdf {
  pdf: {
    templateGet: {
      useQuery: (
        input: { id: string },
        opts?: unknown
      ) => {
        data?: {
          id: string
          name: string
          description?: string | null
          template: Record<string, unknown>
          pageSettings?: Record<string, unknown>
          sampleData?: Record<string, unknown> | null
          isSystem?: boolean
        }
        isLoading: boolean
        error?: Error | null
      }
    }
    templateUpdate: {
      useMutation: () => {
        mutateAsync: (input: {
          id: string
          name: string
          description?: string | null
          template: Record<string, unknown>
          pageSettings?: Record<string, unknown>
          sampleData?: Record<string, unknown> | null
        }) => Promise<{ id: string }>
      }
    }
    // Serverseitiges Rendern für den „Echtes PDF erzeugen"-Knopf der
    // Vorschau. Nimmt das Template inline entgegen — der ungespeicherte
    // Editor-Stand wird also 1:1 gerendert.
    pdfPreview: {
      useMutation: () => {
        mutateAsync: (input: {
          template: Record<string, unknown>
          data: Record<string, unknown>
          options?: { pdfA3?: boolean }
        }) => Promise<{ success: boolean; data?: string }>
      }
    }
  }
  useUtils: () => {
    pdf: {
      templateGet: {
        setData: (input: { id: string }, updater: (prev: unknown) => unknown) => void
        invalidate: (input?: { id: string }) => Promise<void>
      }
    }
  }
}

const trpcPdf = trpc as unknown as TrpcWithPdf

export function PdfTemplateEditorV2Page() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  // Plan CORE 09-17/04: jeder Festtext dieser Seite liest `pdfEditorHost.*` aus
  // `public/locales/<lang>/common.json`; der Rueckfall ist der bisherige deutsche
  // Satz, damit ein fehlender Schluessel nie als Schluesselname erscheint. Der
  // Navigations-Waechter unten liest wie bisher `editorGuard.*`.
  const { t } = useTranslation()

  const query = trpcPdf.pdf.templateGet.useQuery(
    { id: id ?? '' },
    typeof id === 'string' && id.length > 0 ? undefined : { enabled: false }
  )
  const updateMutation = trpcPdf.pdf.templateUpdate.useMutation()
  const previewMutation = trpcPdf.pdf.pdfPreview.useMutation()
  const pdfUtils = trpcPdf.useUtils()

  // Auto-promote on save: every payload the user persists from this
  // editor gets stamped `editorVersion: 'v2'`. Even though EditorApp's
  // template-io already does that (template-io.ts:42), we belt-and-
  // braces it here so legacy paths or test mocks can't slip through.
  const apiClient = useMemo<TemplateApiClient>(() => {
    const sync = (templateId: string, parsed: BlockTemplate) => {
      pdfUtils.pdf.templateGet.setData({ id: templateId }, (prev) => {
        if (!prev || typeof prev !== 'object') return prev
        return {
          ...(prev as Record<string, unknown>),
          template: parsed,
          name: parsed.name,
          description: (parsed as { description?: string }).description || null,
          pageSettings: parsed.page,
        }
      })
    }
    const persist = async (templateId: string, payload: string) => {
      const parsedRaw = JSON.parse(payload) as BlockTemplate
      const parsed: BlockTemplate = { ...parsedRaw, editorVersion: 'v2' }
      await updateMutation.mutateAsync({
        id: templateId,
        name: parsed.name,
        // Editor-Feld ist die Quelle: leerer String leert die DB-Spalte.
        description: (parsed as { description?: string }).description || null,
        template: parsed as unknown as Record<string, unknown>,
        pageSettings: parsed.page as unknown as Record<string, unknown>,
        sampleData: null,
      })
      sync(templateId, parsed)
    }
    return {
      saveDraft: persist,
      publish: persist,
    }
  }, [updateMutation, pdfUtils])

  // Serverseitiges Rendern für den „Echtes PDF erzeugen"-Knopf. Läuft
  // nur auf Klick: `pdf.pdfPreview` prüft und meldet Kontingent-
  // Verbrauch (Entitlement `pdf:template:preview`, type `usage`).
  // Fehler werden absichtlich durchgereicht — die Servermeldung ist die
  // eigentliche Information (fehlende Pflichtfelder, fehlendes
  // Entitlement bei der Viewer-Rolle, …).
  const renderApiClient = useMemo<RenderApiClient>(
    () => ({
      render: async (template, data, options) => {
        const result = await previewMutation.mutateAsync({
          template: template as unknown as Record<string, unknown>,
          data,
          // Zahnrad-Optionen der Vorschau (aktuell PDF/A-3) 1:1 an die
          // Prozedur weiterreichen; `undefined` = Server-Default.
          options: options?.pdfA3 === undefined ? undefined : { pdfA3: options.pdfA3 },
        })
        if (!result.success || !result.data) {
          throw new Error(t('pdf.editor.serverRenderFailed', 'PDF konnte nicht erzeugt werden.'))
        }
        return result.data
      },
    }),
    [previewMutation, t]
  )

  // Warn-modal state. Only meaningful for v1 templates that the user
  // opened in the new editor; v2 templates skip the gate.
  const [v1WarnAccepted, setV1WarnAccepted] = useState(false)

  // Resolve the loaded template into a discriminated shape so the
  // render logic stays linear. MUST sit above the early returns so
  // hook count stays stable across loading → loaded.
  const stored = query.data?.template
  const templateName = query.data?.name
  const storedDescription = query.data?.description
  const resolved = useMemo<
    | { kind: 'pending' }
    | { kind: 'block'; template: BlockTemplate; isV1: boolean }
    | { kind: 'legacy' }
  >(() => {
    if (!stored && stored !== null) return { kind: 'pending' }
    const isV2Shape =
      !!stored && typeof stored === 'object' && 'version' in stored && 'body' in stored
    if (isV2Shape) {
      // Die Beschreibung lebt als DB-Spalte, nicht im JSONB — fürs
      // Editor-Feld hier ins Template gespiegelt (Muster templateType).
      const tpl = {
        ...(stored as unknown as BlockTemplate),
        description: storedDescription ?? undefined,
      } as BlockTemplate
      const isV1 = tpl.editorVersion !== 'v2'
      return { kind: 'block', template: tpl, isV1 }
    }
    const isEmpty = !stored || (typeof stored === 'object' && Object.keys(stored).length === 0)
    if (!isEmpty) return { kind: 'legacy' }
    // Freshly-created template (empty `{}` in DB). Seed a minimal v2
    // BlockTemplate so the user can start editing immediately. Once
    // the first auto-save fires the row in the DB will carry
    // editorVersion: 'v2'.
    return {
      kind: 'block',
      isV1: false,
      template: {
        version: '1.0.0',
        name: templateName || t('pdfEditorHost.newTemplateName', 'Neues Template'),
        description: storedDescription ?? undefined,
        page: {
          size: 'A4',
          orientation: 'portrait',
          margins: { top: 20, right: 20, bottom: 20, left: 20 },
        },
        body: { blocks: [] },
        editorVersion: 'v2',
      },
    }
  }, [stored, templateName, storedDescription, t])

  if (!id) {
    return (
      <div className="flex h-screen items-center justify-center text-sm text-muted-foreground">
        {t('pdfEditorHost.missingId', 'Keine Template-ID in der URL.')}
      </div>
    )
  }
  if (query.isLoading) {
    return (
      <div className="flex h-screen items-center justify-center text-sm text-muted-foreground">
        {t('pdfEditorHost.loading', 'Lädt…')}
      </div>
    )
  }
  if (query.error || !query.data) {
    return (
      <div className="flex h-screen items-center justify-center text-sm text-red-600">
        {query.error?.message ?? t('pdfEditorHost.notFound', 'Template nicht gefunden')}
      </div>
    )
  }

  if (resolved.kind === 'legacy') {
    return (
      <div className="container mx-auto max-w-xl py-12 text-center text-sm text-amber-700 space-y-3">
        <p>
          {t(
            'pdfEditorHost.craftJsLocked',
            'Dieses Template wurde mit dem alten Craft.js-Editor gespeichert und kann im neuen Editor nicht direkt geöffnet werden. Bitte lege ein neues Template an.'
          )}
        </p>
        <button
          type="button"
          onClick={() => navigate('/pdf/v2/templates')}
          className="rounded border border-amber-300 bg-white px-3 py-1.5 text-amber-700 hover:bg-amber-50"
          data-testid="legacy-back-to-list"
        >
          {t('pdfEditorHost.backToList', 'Zurück zur Übersicht')}
        </button>
      </div>
    )
  }
  if (resolved.kind === 'pending') {
    return (
      <div className="flex h-screen items-center justify-center text-sm text-muted-foreground">
        {t('pdfEditorHost.loading', 'Lädt…')}
      </div>
    )
  }

  // v1 template opened in v2 editor → require an explicit OK before
  // mounting EditorApp, so the user understands that saving promotes
  // the template to v2 and the old editor can no longer touch it.
  if (resolved.isV1 && !v1WarnAccepted) {
    return (
      /*
       * Die Huelle ist `ModalShell` (core-frontend), nicht mehr handgerollt:
       * „Abbrechen"/„Mit Bearbeitung fortfahren" stehen im Fuss-Band und
       * bleiben in jeder Schirmhoehe erreichbar. Plan CORE 09-05/02.
       *
       * Verhalten unveraendert: kein `onClose` — der Hintergrund schloss hier
       * auch vorher nicht, und dieses Tor DARF sich nicht per Escape aufheben
       * lassen: dahinter wird ein v1-Template unwiderruflich nach v2 gehoben.
       * Der Weg hinaus sind die zwei Knoepfe.
       *
       * `data-testid="v1-to-v2-warn-modal"` sass schon vorher am Panel und
       * bleibt dort (ModalShell reicht unbekannte Props ans Panel durch).
       */
      <ModalShell
        open
        size="sm"
        testIdPrefix="v1-to-v2-warn"
        data-testid="v1-to-v2-warn-modal"
        className="bg-card"
        backdropClassName="p-4"
        header={
          <h2 className="text-base font-semibold text-foreground">
            {t('pdfEditorHost.v1Modal.title', 'Hinweis')}
          </h2>
        }
        footerClassName="mt-4 items-center gap-2"
        footer={
          <>
            <button
              type="button"
              onClick={() => navigate('/pdf/v2/templates')}
              className="rounded border border-border bg-card px-3 py-1.5 text-sm text-foreground hover:bg-muted"
              data-testid="v1-to-v2-warn-cancel"
            >
              {t('pdfEditorHost.v1Modal.cancel', 'Abbrechen')}
            </button>
            <button
              type="button"
              onClick={() => setV1WarnAccepted(true)}
              className="rounded border border-primary bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90"
              data-testid="v1-to-v2-warn-confirm"
            >
              {t('pdfEditorHost.v1Modal.continue', 'Mit Bearbeitung fortfahren')}
            </button>
          </>
        }
      >
        <p className="text-sm text-muted-foreground">
          {t(
            'pdfEditorHost.v1Modal.body',
            'Dieses Template wurde mit dem alten Editor erstellt (v1). Wenn du es speicherst, wird es in das neue Format (v2) konvertiert und kann anschließend nicht mehr mit dem alten Editor bearbeitet werden.'
          )}
        </p>
      </ModalShell>
    )
  }

  const blockTemplate = resolved.template
  const isSystem = query.data?.isSystem === true

  return (
    <EditorWithGuard
      id={id}
      blockTemplate={blockTemplate}
      apiClient={apiClient}
      renderApiClient={renderApiClient}
      isSystem={isSystem}
    />
  )
}

/**
 * EditorApp + in-app navigation guard. Same pattern as the
 * saas-pdf-api host: when the editor's dirty state is true and the
 * user navigates away, a small modal asks "Speichern, Verwerfen oder
 * Abbrechen" before letting the navigation proceed.
 */
function EditorWithGuard(props: {
  id: string
  blockTemplate: BlockTemplate
  apiClient: TemplateApiClient
  renderApiClient: RenderApiClient
  isSystem: boolean
}) {
  const [dirty, setDirty] = useState(false)
  const [saving, setSaving] = useState(false)
  const saveRef = useRef<(() => Promise<void>) | null>(null)
  const blocker = useBlocker(dirty)
  // Editor chrome language: de/at keep German, every other app locale gets
  // the English dictionary (the editor only ships de + en).
  const { t, i18n } = useTranslation()
  const editorLocale = i18n.language?.startsWith('de') || i18n.language === 'at' ? 'de' : 'en'
  const handleSaveAndProceed = useCallback(async () => {
    if (!saveRef.current || blocker.state !== 'blocked') return
    try {
      setSaving(true)
      await saveRef.current()
      blocker.proceed()
    } catch {
      // Leave the dialog open so the user sees the failure.
    } finally {
      setSaving(false)
    }
  }, [blocker])
  return (
    <>
      <EditorApp
        templateId={props.id}
        initialTemplate={props.blockTemplate}
        apiClient={props.apiClient}
        renderApiClient={props.renderApiClient}
        locale={editorLocale}
        readOnly={props.isSystem}
        onDirtyChange={setDirty}
        saveRef={saveRef}
      />
      {/*
       * Die Huelle ist `ModalShell` (core-frontend), nicht mehr handgerollt:
       * die drei Knoepfe stehen im Fuss-Band und bleiben in jeder Schirmhoehe
       * erreichbar. Dieser Dialog hat KEINEN Ausweg ausser ihnen — kein ✕,
       * kein Escape (eine Navigations-Sperre darf sich nicht per Escape
       * aufheben lassen), der Hintergrund schliesst nicht. Plan CORE 09-05/02.
       *
       * Theme-Token statt slate/blue-Festwerten: der Dialog liegt AUSSERHALB
       * der hellen Insel des Editors und folgt dem Dunkelmodus der App. Das
       * bleibt so — `className`/`headerClassName` reichen die Token an
       * ModalShell durch; `bg-card` haelt das Panel weiss auf der grauen Flaeche.
       *
       * `data-testid="editor-navigation-guard"` sitzt jetzt am PANEL statt am
       * Hintergrund; die Kennung bleibt erhalten und wandert nur eine Ebene.
       */}
      {blocker.state === 'blocked' && (
        <ModalShell
          open
          size="sm"
          testIdPrefix="editor-navigation-guard"
          data-testid="editor-navigation-guard"
          className="bg-card"
          backdropClassName="p-4"
          header={
            <h2 className="text-base font-semibold text-foreground">
              {t('editorGuard.title', 'Ungespeicherte Änderungen')}
            </h2>
          }
          footerClassName="mt-4 items-center gap-2"
          footer={
            <>
              <button
                type="button"
                onClick={() => blocker.reset?.()}
                disabled={saving}
                className="rounded border border-border bg-card px-3 py-1.5 text-sm text-foreground hover:bg-muted disabled:opacity-50"
              >
                {t('editorGuard.cancel', 'Abbrechen')}
              </button>
              <button
                type="button"
                onClick={() => blocker.proceed?.()}
                disabled={saving}
                className="rounded border border-destructive/40 bg-card px-3 py-1.5 text-sm text-destructive hover:bg-destructive/10 disabled:opacity-50"
              >
                {t('editorGuard.discard', 'Verwerfen')}
              </button>
              <button
                type="button"
                onClick={() => void handleSaveAndProceed()}
                disabled={saving}
                className="rounded border border-primary bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
              >
                {saving
                  ? t('editorGuard.saving', 'Speichert…')
                  : t('editorGuard.save', 'Speichern')}
              </button>
            </>
          }
        >
          <p className="text-sm text-muted-foreground">
            {t(
              'editorGuard.body',
              'Du verlässt dieses Template mit ungespeicherten Änderungen. Was möchtest du tun?'
            )}
          </p>
        </ModalShell>
      )}
    </>
  )
}
